import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FileSignature, Trash2, Download, Send, Eye, Upload, Loader2 } from 'lucide-react';
import { formatDate, statusColor } from '../../lib/utils';
import { useDropzone } from 'react-dropzone';

const API = '/api';

export default function ESignDashboard() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const fetchDocs = () => {
    axios.get(`${API}/documents/`)
      .then(r => setDocs(r.data))
      .catch(() => setError('Failed to load documents'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchDocs(); }, []);

  const onDrop = useCallback(async (acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!file) return;
    setUploading(true);
    setError('');
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post(`${API}/documents/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      navigate(`/esign/editor/${res.data.id}`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Upload failed');
      setUploading(false);
    }
  }, [navigate]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'application/pdf': ['.pdf'] },
    maxFiles: 1,
  });

  const deleteDoc = async (id) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      await axios.delete(`${API}/documents/${id}`);
      setDocs(d => d.filter(x => x.id !== id));
    } catch {
      alert('Delete failed');
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
      <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2 mb-6">
        <FileSignature className="w-6 h-6 text-blue-600" /> eSign Documents
      </h1>

      {/* Upload zone */}
      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-xl p-10 text-center cursor-pointer mb-8 transition-colors ${
          isDragActive
            ? 'border-blue-500 bg-blue-50'
            : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'
        }`}
      >
        <input {...getInputProps()} />
        {uploading ? (
          <div className="flex flex-col items-center gap-2 text-blue-600">
            <Loader2 className="w-8 h-8 animate-spin" />
            <span className="font-medium">Uploading...</span>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2 text-gray-500">
            <Upload className="w-10 h-10 text-gray-400" />
            <span className="font-medium text-gray-700">
              {isDragActive ? 'Drop your PDF here' : 'Drop a PDF here or click to browse'}
            </span>
            <span className="text-sm">PDF files only · Max 25MB</span>
          </div>
        )}
      </div>

      {error && (
        <div className="bg-red-50 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm">{error}</div>
      )}

      {/* Document list */}
      {loading ? (
        <div className="text-center py-16">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-gray-400" />
        </div>
      ) : docs.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <FileSignature className="w-14 h-14 mx-auto mb-3 text-gray-300" />
          <p>No documents yet. Upload your first PDF above.</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-200 divide-y divide-gray-100">
          {docs.map(doc => (
            <div key={doc.id} className="flex items-center gap-4 px-6 py-4">
              <FileSignature className="w-8 h-8 text-blue-500 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900 truncate">{doc.filename}</p>
                <p className="text-xs text-gray-400">
                  {formatDate(doc.created_at)} · {doc.signed_count}/{doc.signer_count} signed
                </p>
              </div>
              <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium capitalize flex-shrink-0 ${statusColor(doc.status)}`}>
                {doc.status}
              </span>
              <div className="flex items-center gap-1 flex-shrink-0">
                <Link
                  to={`/esign/editor/${doc.id}`}
                  className="p-1.5 text-gray-400 hover:text-blue-600 transition-colors"
                  title="Edit fields"
                >
                  <Eye className="w-4 h-4" />
                </Link>
                {doc.status === 'draft' && (
                  <Link
                    to={`/esign/send/${doc.id}`}
                    className="p-1.5 text-gray-400 hover:text-green-600 transition-colors"
                    title="Send for signing"
                  >
                    <Send className="w-4 h-4" />
                  </Link>
                )}
                {doc.status === 'completed' && (
                  <a
                    href={`${API}/documents/${doc.id}/download`}
                    className="p-1.5 text-gray-400 hover:text-blue-600 transition-colors"
                    title="Download completed PDF"
                  >
                    <Download className="w-4 h-4" />
                  </a>
                )}
                <button
                  onClick={() => deleteDoc(doc.id)}
                  className="p-1.5 text-gray-400 hover:text-red-500 transition-colors"
                  title="Delete"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
